//@ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Stack,
  Button,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import {
  DeleteConfirmModal,
  Table,
} from "../../molecules";
import { Loading } from "../../atoms";
import {
  IResponseCode,
  useGetResponseCodes,
  useDeleteResponseCode,
  useGetResourceList,
  useGetBankList,
  useGetMemberVPosList
} from "../../../hooks";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { PagingResponse } from "../../../hooks/_types";
import { ResponseCodeModal } from "./ResponseCodeModal";
import { createFilterHandler } from "../../../util/tableQueryBuilder";
import { RequestFilterValueTypeEnums } from "../../../common/enums/RequestFilterEnums";
import { useForm, Controller } from "react-hook-form";
import BaseFilterTableWithAccordion from "../../atoms/BaseFilterTableWithAccordion";
import { FlareSharp } from "@mui/icons-material";
import { downloadExcel } from "../../../util/downloadExcel";

export const ResponseCodeTable = () => {
  const [tableData, setTableData] =
    useState<PagingResponse<Array<IResponseCode>>>();
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });

  const [queryOptions, setQueryOptions] = useState({});
  const previousQueryParams = useRef({});
  const [dynamicFilters, setDynamicFilters] = useState<any>([]);
  const [formFilters, setFormFilters] = useState<any>([]);

  const { data, refetch, isLoading } = useGetResponseCodes({
    page: paginationModel.page,
    size: paginationModel.pageSize,
    orderBy: "CreateDate",
    orderByDesc: true,
    filters: [...dynamicFilters, ...formFilters],
    ...queryOptions,
  });

  const { refetch: refetchForExcel } = useGetResponseCodes({
    page: 0,
    size: -1,
    orderBy: "CreateDate",
    orderByDesc: true,
    filters: [...dynamicFilters, ...formFilters],
    ...queryOptions,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const setSnackbar = useSetSnackBar();
  const { mutate: deleteResponseCode, isLoading: isDeleteLoading } =
    useDeleteResponseCode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResponseCode, setSelectedResponseCode] = useState<
    IResponseCode | undefined
  >(undefined);

  const [isFilterAccordionExpanded, setFilterAccordionExpand] = useState(true);
  const [showTable, setShowTable] = useState(false);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      bankCode: "",
      internalCode: "",
    },
  });
  const [bankList, setBankList] = useState([]);

  const { mutate: getMemberVPosList, data: rawMemberVPosList } =
    useGetMemberVPosList();

  useEffect(() => {
    getMemberVPosList(
      {
        orderBy: "CreateDate",
        orderByDesc: true,
        status: "ACTIVE",
      },
      {
        onSuccess: (data) => {
          const bankList = data?.data?.map(
            (bank: { bankCode: string; bankName: string }) => {
              return {
                label: `${bank.bankName}`,
                value: bank.bankCode,
              };
            }
          );
          setBankList(bankList);
        },
      }
    );
  }, [getMemberVPosList]);

  const [internalErrorCodeList, setInternalErrorCodeList] = useState<any[]>([]);
  const { mutate: getResourceList } = useGetResourceList();

  useEffect(() => {
    getResourceList(
      {
        size: 999,
        page: 0,
        orderBy: "CreateDate",
        orderByDesc: true
      },
      {
        onSuccess: (data: any) => {
          const codes =
            data?.data?.result?.map((code: { key: string; value: string }) => ({
              label: `${code.value} (${code.key})`,
              value: code.key,
            })) || [];
          setInternalErrorCodeList(codes);
        },
      }
    );
  }, [getResourceList]);

  useEffect(() => {
    const savedFilters = localStorage.getItem("responseCodeFilters");
    if (savedFilters) {
      setQueryOptions(JSON.parse(savedFilters));
    }
  }, []);

  useEffect(() => {
    if (showTable) {
      refetch();
    }
  }, [
    paginationModel,
    refetch,
    queryOptions,
    dynamicFilters,
    formFilters,
    showTable,
  ]);

  useEffect(() => {
    if (data) {
      setTableData(data as any);
    } else {
      setTableData([] as any);
    }
  }, [data]);

  const handleAdd = () => {
    setSelectedResponseCode(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (row: IResponseCode) => {
    setSelectedResponseCode(row);
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setSelectedRowId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteResponseCode(selectedRowId, {
      onSuccess: (data: any) => {
        if (data.isSuccess) {
          refetch();
          setSnackbar({
            severity: "success",
            isOpen: true,
            description: data.message,
          });
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });
    setIsDeleteModalOpen(false);
  };

  const columns: GridColDef<IResponseCode>[] = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            label="Düzenle"
            onClick={() => handleEdit(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            label="Sil"
            onClick={() => handleDelete(params.row.id)}
            showInMenu
          />,
        ],
      },
      {
        field: "integrationCode",
        headerName: "Banka Kodu",
        width: 150,
        columnType: RequestFilterValueTypeEnums.STRING,
      },
      {
        field: "bankName",
        headerName: "Banka Adı",
        width: 200,
        columnType: RequestFilterValueTypeEnums.STRING,
        filterable: false,
        sortable: false
      },
      {
        field: "errorCode",
        headerName: "Hata Kodu",
        width: 150,
        columnType: RequestFilterValueTypeEnums.STRING,
      },
      {
        field: "errorCodeDesc",
        headerName: "Hata Kodu Açıklaması",
        width: 300,
        columnType: RequestFilterValueTypeEnums.STRING,
      },
      {
        field: "createDate",
        headerName: "Hata Kodu Tarihi",
        width: 180,
        columnType: RequestFilterValueTypeEnums.DATE,
        renderCell: (params) => {
          const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
          if (formattedDate === "Invalid Date") return "";
          return formattedDate;
        },
      },
      {
        field: "internalErrorCode",
        headerName: "İç Hata Kodu",
        width: 150,
        columnType: RequestFilterValueTypeEnums.STRING,
        filterable: false,
        sortable: false
      },
      {
        field: "internalErrorCodeDesc",
        headerName: "İç Hata Kodu Açıklaması",
        width: 300,
        columnType: RequestFilterValueTypeEnums.STRING,
        filterable: false,
        sortable: false
      },
    ],
    []
  );

  const handleFilterChange = createFilterHandler({
    setDynamicFilter: setDynamicFilters,
    setQueryOptions,
    previousQueryParams,
    columns,
    filterStorageKey: "responseCodeFilters",
    isValueValid: (key, value) => value?.toString().length >= 1,
  });

  const { mutate: getResponseCodesForExcel } = useGetResponseCodes();

  const onSubmit = (data: { bankCode: string; internalCode: string }) => {
    const newFilters: any[] = [];
    if (data.bankCode) {
      newFilters.push({
        field: "integrationCode",
        values: [data.bankCode],
        operationType: 1, // equals
        valueType: 1, // string
      });
    }
    if (data.internalCode) {
      newFilters.push({
        field: "internalErrorCode",
        values: [data.internalCode],
        operationType: 1, // equals
        valueType: 1,
      });
    }
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setFormFilters(newFilters);
    setFilterAccordionExpand(false);
    setShowTable(true);
  };

  const onSave = async () => {
    try {
      const result = await refetchForExcel();
      
      if (result.data?.result) {
        const excelData = result.data.result.map((item: IResponseCode) => {
          const formattedDate = item.createDate ? dayjs(item.createDate).format("DD.MM.YYYY HH:mm") : "";
          return {
            "Banka Kodu": item.integrationCode,
            "Banka Adı": item.bankName,
            "Hata Kodu": item.errorCode,
            "Hata Kodu Açıklaması": item.errorCodeDesc,
            "Hata Kodu Tarihi": formattedDate,
            "İç Hata Kodu": item.internalErrorCode,
            "İç Hata Kodu Açıklaması": item.internalErrorCodeDesc,
          };
        });

        downloadExcel(excelData, "Banka_Hata_Kodlari");

        setSnackbar({
          severity: "success",
          description: `${result.data.result.length} kayıt Excel'e aktarıldı`,
          isOpen: true,
        });
      } else {
        setSnackbar({
          severity: "info",
          description: "İndirilecek veri bulunamadı",
          isOpen: true,
        });
      }
    } catch (error) {
      setSnackbar({
        severity: "error",
        description: "Excel indirme sırasında hata oluştu",
        isOpen: true,
      });
    }
  };

  return (
    <>
      {(isLoading || isDeleteLoading) && <Loading />}
      <Stack spacing={2}>
        <BaseFilterTableWithAccordion
          summary="Hata Kodu Filtrele"
          isExpanded={isFilterAccordionExpanded}
          setExpanded={setFilterAccordionExpand}
          setTableVisibility={setShowTable}
          child={
            <Stack spacing={2} p={2}>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  {bankList && (
                    <Controller
                      name="bankCode"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value } }) => {
                        const selectedBank = bankList.find(
                          (option: any) => option.value === value
                        );
                        return (
                          <Autocomplete
                            id="bankCode"
                            options={bankList}
                            getOptionLabel={(option) => option.label}
                            value={selectedBank || null}
                            onChange={(_, newValue) => {
                              onChange(newValue ? newValue.value : "");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Banka" />
                            )}
                          />
                        );
                      }}
                    />
                  )}
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  {internalErrorCodeList && (
                    <Controller
                      name="internalCode"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value } }) => {
                        const selectedCode = internalErrorCodeList.find(
                          (option: any) => option.value === value
                        );
                        return (
                          <Autocomplete
                            id="internalCode"
                            options={internalErrorCodeList}
                            getOptionLabel={(option) => option.label}
                            value={selectedCode || null}
                            onChange={(_, newValue) => {
                              onChange(newValue ? newValue.value : "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="İç Hata Kodu"
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )}
                </FormControl>
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Button onClick={handleSubmit(onSubmit)} variant="contained">
                  ARA
                </Button>
              </Stack>
            </Stack>
          }
        />

        {showTable && (
          <>
            <Button
              variant="contained"
              onClick={handleAdd}
              sx={{ alignSelf: "flex-end", width: "fit-content" }}
            >
              Yeni Hata Kodu Ekle
            </Button>
            <Table
              rows={tableData?.result || []}
              columns={columns}
              rowCount={tableData?.totalItems || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              paginationMode="server"
              columnVisibilityConfigKey="responseCodeColumnVisibility"
              filterKey="responseCodeFilters"
              handleFilterChange={handleFilterChange}
              hasActiveFilters={Object.keys(queryOptions).length > 0}
              onSave={onSave}
            />
          </>
        )}
      </Stack>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
      <ResponseCodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        responseCode={selectedResponseCode}
        refetch={refetch}
      />
    </>
  );
};